import { cx } from "@flexbyt/widget-preview";
import { CUSTOMER_LOGOS, CUSTOMER_LOGO_BASE } from "../../content/customers.js";

/**
 * The businesses already using ReviewsJet, scrolling.
 *
 * ===========================================================================
 * CSS, not `react-fast-marquee`
 * ===========================================================================
 * The outgoing site used `react-fast-marquee`, and this site already carries
 * that library for the marquee *widget*. It is the wrong tool here, for a
 * reason the README documents at length: the component measures its container
 * before deciding how much content to draw, and there is nothing to measure on
 * a server, so it renders `null`.
 *
 * A widget that only appears after hydration is a known limit. A *trust strip*
 * that only appears after hydration is worse than that — it is the first thing
 * on the page that answers "are these people real", and it would be absent from
 * the HTML a crawler reads and from the first paint a visitor sees. So this is
 * a duplicated track and one `@keyframes` translation: it is in the HTML, it
 * needs no JavaScript, and it cannot render nothing.
 *
 * ===========================================================================
 * Why the track is duplicated
 * ===========================================================================
 * The animation moves the flex row left by exactly half its own width. Because
 * the second half is a copy of the first, the frame at -50% is pixel-identical
 * to the frame at 0%, so the loop has no seam. That also makes it degrade
 * correctly under `prefers-reduced-motion`: the global rule in `app.css` cuts
 * every animation to one iteration of 0.01ms, which lands this at -50% — a
 * still, complete row of logos rather than a blank strip or a jitter.
 *
 * The copy is `aria-hidden`, so the same businesses are not announced twice.
 *
 * ===========================================================================
 * It only scrolls when there is something to scroll
 * ===========================================================================
 * The strip was written for a dozen logos, where one set comfortably outran any
 * screen and motion was the only way to show them all. It has at times carried
 * as few as three, and three logos are about 700px, which runs out mid-monitor.
 *
 * Padding that out by repeating the set does fill the row, and it looks exactly
 * like what it is: the same two logos passing twice in one screen, under a line
 * that claims thousands of customers. A marquee says "there are more of these
 * than fit". Three logos cycling says the opposite louder than the copy does.
 *
 * So below the width of one screen the row is drawn once, centred and still.
 * It reads as a deliberate short list rather than a broken loop, and the moment
 * enough logos come back to fill a screen the animation returns on its own.
 *
 * ===========================================================================
 * The logos are muted on purpose
 * ===========================================================================
 * Logos drawn by different designers at different weights will fight each
 * other and the page if shown as supplied. Greyscale and a single opacity step
 * is the standard treatment because it makes the row read as one texture
 * rather than as a set of competing marks. It also keeps them from
 * out-shouting the headline immediately above.
 */

/** One pass of the row. Slow enough to read a logo, not so slow it looks stuck. */
const DURATION = "46s";

/** Past a 1440 layout with room to spare, so no screen sees the row end. */
const HALF_MIN_WIDTH = 1800;

/** The `gap-16` between logos at the size this renders at. */
const GAP = 64;

/**
 * How wide one set of logos draws.
 *
 * Rendered height is fixed at 28px and width follows, so the intrinsic size in
 * `customers.ts` gives the drawn width without measuring anything.
 */
const SET_WIDTH = CUSTOMER_LOGOS.reduce(
  (total, logo) => total + (logo.width * 28) / logo.height + GAP,
  0,
);

/**
 * Whether one set of logos is wide enough that scrolling is the honest way to
 * show them. Decided here rather than in a stylesheet so the markup itself
 * differs: a still row has no duplicate track to hide from screen readers.
 */
const SCROLLS = SET_WIDTH >= HALF_MIN_WIDTH;

export function CustomerMarquee({ className }: { className?: string }) {
  return (
    <section
      aria-label="Businesses using ReviewsJet"
      className={cx("overflow-hidden", className)}
    >
      <p className="text-center text-caption text-ink-500">
        Trusted by thousands of businesses around the world
      </p>

      {/*
        The edge fade.

        A hard-cut marquee reads as content clipped by a box; a fade reads as a
        row that carries on past the page. `mask-image` rather than two absolute
        gradient overlays, because overlays have to know the colour behind them
        — and this strip sits on white here and would sit on `ink-25` if it ever
        moved section.
      */}
      {SCROLLS ? (
        <div className="mt-6 [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
          <div
            className="flex w-max animate-[rj-marquee_var(--rj-marquee-duration)_linear_infinite] items-center gap-12 sm:gap-16"
            style={{ "--rj-marquee-duration": DURATION } as React.CSSProperties}
          >
            <Track />
            {/* The seamless second half. Same logos, announced once. */}
            <Track aria-hidden />
          </div>
        </div>
      ) : (
        /* Still and centred. No mask, because nothing runs past the edge to
           fade, and no second track, because there is no loop to hide a seam
           in. */
        <div className="mt-6 flex flex-wrap items-center justify-center gap-10 sm:gap-16">
          <Track />
        </div>
      )}
    </section>
  );
}

/**
 * One full set of logos.
 *
 * `contents` so the two tracks' children sit in the *parent* flex row and share
 * its gap. A wrapper element here would put a different, unstyled gap at the
 * join between the two halves, and the seam would be visible once per loop —
 * the one artefact this pattern is supposed to avoid.
 */
function Track({ "aria-hidden": hidden }: { "aria-hidden"?: boolean }) {
  return (
    <div className="contents" aria-hidden={hidden}>
      {CUSTOMER_LOGOS.map((logo) => (
        <img
          key={logo.file}
          src={`${CUSTOMER_LOGO_BASE}${logo.file}`}
          alt={hidden ? "" : logo.name}
          width={logo.width}
          height={logo.height}
          loading="lazy"
          decoding="async"
          /*
            Height is fixed and width follows, so logos supplied at different
            intrinsic sizes all sit on one optical line. `w-auto` is load-bearing
            — without it the `width` attribute wins and the tall one squashes.
          */
          className="h-6 w-auto shrink-0 opacity-75 grayscale sm:h-7"
        />
      ))}
    </div>
  );
}
